import { MODES, MODE_KEYS, TARGET_EMOJI, THROWABLES, type Mode, type Pairing } from '../game/engine';
import { fetchStats, type Stats } from '../net/api';
import type { Player } from '../player';
import { el } from './dom';

function fmtTime(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  return `${(m / 60).toFixed(1)} h`;
}

/** a player's lifetime numbers: rounds, bests per mode, what they threw at what */
export function renderStats(root: HTMLElement, player: Player, nav: { back(): void; signIn(): void }): () => void {
  const body = el('div', { class: 'stats-body' });
  const status = el('p', { class: 'dim center' }, 'Loading…');
  const wrap = el(
    'section',
    { class: 'page stats' },
    el('header', { class: 'page-top' }, el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'back', onclick: nav.back }, '‹'), el('h1', {}, player.name || 'Your stats'), el('span', { class: 'icon-btn', style: 'visibility:hidden' })),
    status,
    body,
  );
  root.append(wrap);

  const tile = (big: string, label: string): HTMLElement => el('div', { class: 'stat' }, el('b', {}, big), el('span', {}, label));

  async function load(): Promise<void> {
    const s: Stats | null = await fetchStats(player.id);
    if (!s) {
      status.textContent = player.name ? 'Could not reach the server, or nothing posted yet.' : 'Post a round with a name first.';
      return;
    }
    status.hidden = true;
    const thrown: Record<string, number> = {};
    const hitOn: Record<string, number> = {};
    for (const [key, n] of Object.entries(s.tally) as [Pairing, number][]) {
      const [kind, target] = key.split(':');
      thrown[kind] = (thrown[kind] ?? 0) + n;
      hitOn[target] = (hitOn[target] ?? 0) + n;
    }
    const favourite = Object.entries(thrown).sort((a, b) => b[1] - a[1])[0];
    const since = s.first ? new Date(s.first * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
    const parts: (HTMLElement | null)[] = [
      el('p', { class: 'dim' }, s.reserved ? `Reserved name. Playing since ${since ?? 'today'}.` : `This device only; reserve the name to keep stats across phones.`),
      el('div', { class: 'stats-grid' },
        tile(String(s.rounds), 'rounds'),
        tile(String(s.shots), 'things thrown'),
        tile(String(s.hits), 'coconuts hit'),
        tile(`${s.distance} m`, 'total distance'),
        tile(`${Math.round(s.farthest)} m`, 'farthest'),
        tile(fmtTime(s.seconds), 'on the water'),
        tile(s.shots ? `${Math.round((100 * s.hits) / s.shots)}%` : '0%', 'accuracy'),
        tile(favourite ? THROWABLES[favourite[0] as keyof typeof THROWABLES].emoji : '–', favourite ? `favourite (${favourite[1]} landed)` : 'favourite'),
      ),
      el('h2', {}, 'Best by mode'),
      el('ol', { class: 'board-list' }, ...MODE_KEYS.filter((m): m is Mode => !!MODES[m].game && !!s.games[MODES[m].game!]).map((m) => {
        const g = s.games[MODES[m].game!];
        return el('li', { class: 'board-row' }, el('span', { class: 'rank' }, '🏆'), el('span', { class: 'name' }, MODES[m].label), el('span', { class: 'score' }, String(g.best)), el('small', { class: 'meta' }, `${g.rounds} rounds · ${g.hits} 🥥 · last ${g.last}`));
      })),
      Object.keys(hitOn).length ? el('h2', {}, 'What you hit') : null,
      Object.keys(hitOn).length
        ? el('p', { class: 'tally-line' }, (Object.entries(hitOn) as [keyof typeof TARGET_EMOJI, number][]).sort((a, b) => b[1] - a[1]).map(([t, n]) => `${TARGET_EMOJI[t]} ${n}`).join('   '))
        : null,
      s.reserved ? null : el('button', { class: 'btn', type: 'button', onclick: nav.signIn }, 'Reserve this name with a PIN'),
    ];
    body.replaceChildren(...parts.filter((x): x is HTMLElement => x !== null));
  }
  void load();
  return () => wrap.remove();
}
